import { useEffect } from 'react'
import { queryClient } from '~/common/react-query/queryClient'
import { Badge, Button, Heading, Stack, Text, RegionError } from '~/design_system'
import { useAccountMeQuery } from '~/api/accounts'
import { useHomeMessagesQuery, HOME_MESSAGES_QUERY_KEY } from '~/api/home'
import { formatLocalDateTime } from '~/common/dateUtils'
import { useMsg } from '~/common/localizationUtils'
import { CardBody, CardFooter, CardHeader, CardHeading } from '~/components/v2/Card'
import { ItemsList, ItemsListItem, ItemsListItems, ItemsListLink } from '~/components/v2/ItemsList'
import { UNREAD_BG_COLOR, WidgetEmptyState } from '~/home/common'
import { MessageBubble } from '~/icons'
import { $thm } from '~/common/themeSx'

/**
 * @param props {Record}
 * @param props.setTabIndicator {(val: boolean | string) => void}
 * @returns {JSX.Element}
 */
export function Messages({ setTabIndicator = () => {} }) {
  const {
    data: { conv, message_count, is_in_app_messaging_disabled } = {},
    isLoading,
    isError
  } = useHomeMessagesQuery()
  const { data: { role } = {} } = useAccountMeQuery()
  const msg = useMsg()

  useEffect(() => {
    setTabIndicator(message_count > 0)
  }, [message_count])

  const inboxHref = `/messages/${role}/inbox/?`
  return (
    <>
      <CardHeader sx={$thm({ pt: { $canvas: 6 } })}>
        <CardHeading>
          {msg('message.card.new_messages')}
          {!isLoading && (
            <Badge display="inline" color="gray.500" ml={4} fontSize="md">
              {message_count}
            </Badge>
          )}
        </CardHeading>
      </CardHeader>
      {!isLoading && isError && (
        <RegionError onRefresh={() => queryClient.invalidateQueries(HOME_MESSAGES_QUERY_KEY)} />
      )}
      {!isLoading && !isError && message_count === 0 && (
        <WidgetEmptyState icon={MessageBubble} mt={0}>
          <Text textStyle="bodySemi">{msg('message.card.no_new_messages')}</Text>
        </WidgetEmptyState>
      )}

      {message_count > 0 && (
        <CardBody p={0}>
          <ItemsList border={0}>
            <ItemsListItems>
              {conv?.map(p => (
                <Message key={p.id} {...p} />
              ))}
              {message_count > conv.length && (
                <ItemsListItem py={4}>
                  <ItemsListLink href={inboxHref} display="block" textAlign="center" py={4} my={-4}>
                    {msg('common.ui.view_all')}
                  </ItemsListLink>
                </ItemsListItem>
              )}
            </ItemsListItems>
          </ItemsList>
        </CardBody>
      )}

      {!is_in_app_messaging_disabled && (
        <CardFooter justifyContent="center">
          <Button variant="primary" href={inboxHref}>
            {msg('message.card.inbox.button')}
          </Button>
        </CardFooter>
      )}
    </>
  )
}

function Message({ id, cto, cfrom, message, created_at }) {
  const { data: { role } = {} } = useAccountMeQuery()
  return (
    <ItemsListItem key={id} py={4} bg={UNREAD_BG_COLOR}>
      <ItemsListLink href={`/messages/${role}/inbox/?id=${id}`} py={4} my={-4}>
        <Stack isInline spacing={4}>
          <MessageBubble color="gray.500" />
          <Stack spacing={0}>
            <Heading fontSize="md">{role === 'vendor' ? cfrom.first_name : cto.first_name}</Heading>
            <Text>{message}</Text>
            <Text fontSize="sm" color="gray.500">
              {formatLocalDateTime(created_at)}
            </Text>
          </Stack>
        </Stack>
      </ItemsListLink>
    </ItemsListItem>
  )
}
